import { useState, useEffect, useRef, RefObject, useCallback } from "react";

/**
 * Custom hook to detect container width for responsive design
 * Works in preview mode by using ResizeObserver instead of @media queries
 * In editor mode (no preview container), uses viewport width
 */
export function useResponsive(elementRef?: RefObject<HTMLElement>) {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const internalRef = useRef<HTMLElement>(null);
  const lastWidthRef = useRef<number>(0);

  const ref = elementRef || internalRef;

  // Find preview container once and cache it
  const findPreviewContainer = useCallback((element: HTMLElement): HTMLElement | null => {
    let container: HTMLElement | null = element;

    while (container && container.parentElement) {
      const parent: HTMLElement = container.parentElement;
      // Check for preview container (has containerType or specific class)
      const style = window.getComputedStyle(parent);
      if (style.containerType === "inline-size" || style.containerType === "size") {
        return parent;
      }
      // Also check for preview wrapper by class or data attribute
      if (parent.classList.contains("preview-container") || parent.hasAttribute("data-preview-mode")) {
        return parent;
      }
      container = parent;
    }

    return null;
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Find preview container once
    const previewContainer = findPreviewContainer(element);

    const checkWidth = () => {
      let width: number;

      if (previewContainer) {
        // In preview mode - use the preview container's width
        width = previewContainer.clientWidth;
        setIsPreviewMode(true);
      } else {
        // In editor mode - use viewport width to match real-world behavior
        width = window.innerWidth;
        setIsPreviewMode(false);
      }

      // Only update state if width actually changed (prevents unnecessary re-renders)
      if (width !== lastWidthRef.current) {
        lastWidthRef.current = width;
        const newIsMobile = width < 768;
        const newIsTablet = width >= 768 && width < 1024;
        setContainerWidth(width);
        setIsMobile(newIsMobile);
        setIsTablet(newIsTablet);
      }
    };

    // Initial check - run synchronously to have correct values on first render
    checkWidth();

    // Observe resize - use RAF to batch updates and prevent loop errors
    let rafId: number | null = null;
    const observer = new ResizeObserver(() => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(checkWidth);
    });

    observer.observe(element);
    if (previewContainer) {
      observer.observe(previewContainer);
    }

    // Also listen for window resize (for editor mode)
    const handleResize = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(checkWidth);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [ref, findPreviewContainer]);

  return {
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    containerWidth,
    isPreviewMode,
    ref: internalRef,
  };
}

/**
 * Helper to get responsive value based on container width
 */
export function getResponsiveValue<T>(
  isMobile: boolean,
  isTablet: boolean,
  values: { xs?: T; sm?: T; md?: T; lg?: T } | T
): T {
  if (
    typeof values !== "object" ||
    values === null ||
    !("xs" in values || "sm" in values || "md" in values || "lg" in values)
  ) {
    return values as T;
  }

  const v = values as { xs?: T; sm?: T; md?: T; lg?: T };

  if (isMobile) {
    return v.xs ?? v.sm ?? v.md ?? v.lg ?? (values as T);
  }
  if (isTablet) {
    return v.sm ?? v.md ?? v.lg ?? v.xs ?? (values as T);
  }
  return v.lg ?? v.md ?? v.sm ?? v.xs ?? (values as T);
}
